import { TurnMargins } from "@pdfking/filling-flex"
import type { Degree, Distance } from "@pdfking/math"
import { AddMargins, ClampMargins, FlipMargins, RemoveMargins } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { GrowContentKind } from "@pdfking/templates"
import { DeleteDuplicates } from "@pdfking/tfunc"

import { DEFAULT_FILE_BLEED, DYNAMIC_OFFSET } from "#constants"
import type { Drawable } from "#internal"

import {
  BLEED_COLOR_ID,
  MEASURE_COLOR_ID,
  PAPER_COLOR_ID,
  SECURITY_COLOR_ID,
} from "../../../data/ColorList"
import { CreateRect } from "../../../shapes/CreateRect"
import { DistanceRound } from "../../../utils/DistanceRound"
import { LeafNodes } from "../layout/LeafNodes"
import { SheetMargins } from "../layout/SheetMargins"
import { PlotMeasureFromGuides } from "../PlotMeasureFromGuides"
import { GrowGuides } from "./GrowGuides"

/**
 * The help marks of a grow where it landed: round every page the box its
 * media bleeds into — the page and the bleed the layout gave it, the very rect
 * the media is masked to — inside every page its security box, when the grow
 * keeps one, and the measures along the grow's guides. Under a fill the
 * measures are left out: they would pile up.
 *
 * The security margins are the page's own — turned over where the page says
 * so, a left-hand page keeping towards the fold what a right-hand one does —
 * turned the way the page landed. A grow laid out as one page — a box, its
 * faces drawn on it — keeps its security inside every face: each of its
 * pages, turned over on the axis the piece says.
 */
export const DrawGrowHelp: Drawable = function* (node, _frame, ctx) {
  const grow = node.data?.grow
  if (!grow) return
  const landed = GrowGuides(node, grow)
  if (!landed) return
  const { canvas } = landed
  const guides = {
    x: landed.guides.x.map(x => (x - canvas.x) as Distance),
    y: landed.guides.y.map(y => (y - canvas.y) as Distance),
  }

  const { security } = grow
  const has_security = security.top || security.right || security.bottom || security.left

  for (const leaf of LeafNodes(node)) {
    yield CreateRect(
      AddMargins(leaf.position, ClampMargins(leaf.bleed, ZERO_DISTANCE, ctx.bleed)),
      { stroke_color: BLEED_COLOR_ID }
    )
    if (has_security && !grow.collapsed) {
      const flip = leaf.data?.page?.flip_margins
      yield CreateRect(
        RemoveMargins(
          leaf.position,
          SheetMargins(leaf, flip ? FlipMargins(security, flip) : security)
        ),
        { stroke_color: SECURITY_COLOR_ID }
      )
    }
  }

  if (has_security && grow.collapsed)
    for (const { box, leaf, page } of landed.cells) {
      if (leaf?.kind !== GrowContentKind.PAGE) continue
      // a piece laid upside down reads its margins upside down too
      const own = leaf.flip ? FlipMargins(security, leaf.flip) : security
      yield CreateRect(
        RemoveMargins(
          box,
          SheetMargins(page, TurnMargins(own, (leaf.meta.rotation ?? 0) as Degree))
        ),
        { stroke_color: SECURITY_COLOR_ID }
      )
    }

  if (ctx.plot_measures) {
    // The canvas edges once each: a page edge on them is a guide already.
    const guides_x = [
      ...DeleteDuplicates([ZERO_DISTANCE, ...guides.x, canvas.width], DistanceRound),
    ]
    const guides_y = [
      ...DeleteDuplicates([ZERO_DISTANCE, ...guides.y, canvas.height], DistanceRound),
    ]
    yield* PlotMeasureFromGuides({
      canvas,
      guides_x,
      guides_y,
      padding: (DYNAMIC_OFFSET + DEFAULT_FILE_BLEED) as Distance,
      stroke_color: MEASURE_COLOR_ID,
      text_color: PAPER_COLOR_ID,
    })
  }
}
